import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, XCircle, Wrench } from "lucide-react";

import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const daysLeft = (target) => {
  if (!target) return null;
  return Math.ceil((new Date(target).getTime() - Date.now()) / 86400000);
};

const REWORK_STAGES = 3;

// Qty still sitting in the Rework Store for this order.
const inRework = (o) => Math.max(0, Number(o.qty || 0) - Number(o.ok_qty || 0) - Number(o.rejected_qty || 0));

const OUTCOME = {
  reworked_ok: { text: "Reworked OK → Final Inspection", tone: "text-emerald-600", Icon: CheckCircle2 },
  reworked_partial_rejected: { text: "Partly rejected", tone: "text-amber-600", Icon: AlertTriangle },
  rejected_after_stage_3: { text: "Rejected Warehouse", tone: "text-red-600", Icon: XCircle },
  failed_retest_rejected: { text: "Rejected Warehouse", tone: "text-red-600", Icon: XCircle },
  passed_retest: { text: "Retest passed", tone: "text-emerald-600", Icon: CheckCircle2 },
};

const STATUS_STYLE = {
  OPEN: "bg-amber-50 text-amber-700",
  COMPLETED: "bg-emerald-50 text-emerald-700",
  REJECTED: "bg-red-50 text-red-700",
};

export default function ProductionReworkOrders() {
  const [orders, setOrders] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ ok_qty: "", not_ok_qty: "", remarks: "", output_item_id: "" });
  const [fiItems, setFiItems] = useState(null);
  const [saving, setSaving] = useState(false);

  const openComplete = (o) => {
    setTarget(o);
    setForm({ ok_qty: String(inRework(o)), not_ok_qty: "0", remarks: "", output_item_id: "" });
    if (fiItems === null) {
      api.get("/items")
        .then(({ data }) => setFiItems((data || []).filter((i) => i.type === "FI")))
        .catch(() => setFiItems([]));
    }
  };

  const submitStage = async () => {
    const ok = Number(form.ok_qty || 0);
    const notOk = Number(form.not_ok_qty || 0);
    const pending = inRework(target);
    if (ok < 0 || notOk < 0) return toast.error("Quantities must be non-negative");
    if (Math.abs(ok + notOk - pending) > 1e-6) return toast.error(`OK + NOT OK must equal the qty in Rework (${pending})`);
    setSaving(true);
    try {
      const body = { ok_qty: ok, not_ok_qty: notOk, remarks: form.remarks };
      if (form.output_item_id) body.output_item_id = form.output_item_id;
      const { data } = await api.post(`/production-rework-orders/${target.id}/complete-stage`, body);
      const o = data.rework_order;
      toast.success(o.status === "OPEN"
        ? `Rework Stage ${o.current_rework_stage - 1} recorded — ${notOk} pcs continue to Stage ${o.current_rework_stage}`
        : `${o.order_no} closed`);
      setTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const load = async () => {
    setOrders(null);
    try {
      const params = statusFilter === "all" ? {} : { status: statusFilter };
      const { data } = await api.get("/production-rework-orders", { params });
      setOrders(data || []);
    } catch (err) {
      setOrders([]);
      toast.error(formatApiError(err?.response?.data?.detail) || err.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  return (
    <div className="space-y-6" data-testid="production-rework-orders">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Quality</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>Rework Orders</h1>
          <p className="text-sm text-slate-500 mt-1">
            Auto-raised alongside a Production NCR on a Testing or Final Inspection QC failure. Rework must be
            completed within 5 working days (Testing) or 2 working days (Final Inspection), in up to 3 Rework
            Stages: OK parts of every stage go to Final Inspection, NOT OK parts go to the next stage — and after
            Stage 3, to the Rejected Warehouse.
          </p>
        </div>
        <Link to="/quality/production-ncrs"><button className="text-sm px-3 py-1.5 rounded-md border border-slate-200 hover:border-blue-300 text-slate-700">Go to Production NCRs</button></Link>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {["all", "OPEN", "COMPLETED", "REJECTED"].map((k) => (
          <button
            key={k}
            onClick={() => setStatusFilter(k)}
            data-testid={`rwo-filter-${k}`}
            className={`text-[11px] px-3 py-1 rounded-full border transition ${statusFilter === k ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200 text-slate-700 hover:border-blue-300"}`}
          >
            {k === "all" ? "All" : k.charAt(0) + k.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Production Rework Orders</div>
        {orders === null && <div className="py-10 text-center text-sm text-slate-500">Loading…</div>}
        {orders && orders.length === 0 && (
          <div className="py-14 text-center text-slate-400">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm">No Rework Orders — nothing has failed QC at Testing or Final Inspection.</p>
          </div>
        )}
        {orders && orders.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="py-2 px-4">Order No</th><th className="py-2 px-4">Job</th><th className="py-2 px-4">Item</th>
                  <th className="py-2 px-4">Stage</th><th className="py-2 px-4">Qty</th><th className="py-2 px-4">NCR</th>
                  <th className="py-2 px-4">Raised</th><th className="py-2 px-4">Due (SLA)</th>
                  <th className="py-2 px-4">Rework Stage</th>
                  <th className="py-2 px-4">Status</th><th className="py-2 px-4">Outcome</th><th className="py-2 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const dleft = daysLeft(o.due_date);
                  const overdue = o.status === "OPEN" && dleft !== null && dleft < 0;
                  return (
                    <tr key={o.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-4 font-mono font-semibold text-blue-700">{o.order_no}</td>
                      <td className="py-2 px-4 mono">{o.job_code || "—"}</td>
                      <td className="py-2 px-4">
                        <div className="mono text-slate-500">{o.item_code || "—"}</div>
                        <div className="text-slate-700">{o.item_name || "—"}</div>
                      </td>
                      <td className="py-2 px-4">{o.stage_label || o.stage}</td>
                      <td className="py-2 px-4 mono">
                        <div className="text-red-600">{o.qty}</div>
                        {(o.ok_qty > 0 || o.rejected_qty > 0) && (
                          <div className="text-[10px] text-slate-500">
                            OK {o.ok_qty || 0} · Rej {o.rejected_qty || 0} · In rework {inRework(o)}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-4 font-mono text-slate-500">{o.ncr_no || "—"}</td>
                      <td className="py-2 px-4">{o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}</td>
                      <td className={`py-2 px-4 ${overdue ? "text-red-600 font-semibold" : ""}`}>
                        {o.due_date ? new Date(o.due_date).toLocaleDateString() : "—"}
                        <span className="text-slate-400"> ({o.sla_working_days}wd)</span>
                        {overdue && <span className="ml-1"><AlertTriangle className="inline w-3 h-3" /> Overdue</span>}
                      </td>
                      <td className="py-2 px-4" data-testid={`rwo-stages-${o.order_no}`}>
                        <div className="flex items-center gap-1">
                          {[1, 2, 3].map((n) => {
                            const r = (o.stage_results || []).find((x) => x.stage === n);
                            const current = o.status === "OPEN" && (o.current_rework_stage || 1) === n;
                            const tone = r ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                              : current ? "bg-amber-50 border-amber-300 text-amber-700"
                              : "bg-white border-slate-200 text-slate-400";
                            return (
                              <span key={n} title={r ? `OK ${r.ok_qty} · NOT OK ${r.not_ok_qty}${r.remarks ? ` · ${r.remarks}` : ""}` : ""}
                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${tone}`}>
                                S{n}{r ? ` ${r.ok_qty}/${r.not_ok_qty}` : ""}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                      <td className="py-2 px-4">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS_STYLE[o.status] || "bg-slate-50 text-slate-700"}`}>
                          {o.status}
                        </span>
                      </td>
                      <td className="py-2 px-4">
                        {(() => {
                          const oc = OUTCOME[o.outcome] || (o.status === "REJECTED" ? OUTCOME.failed_retest_rejected
                            : o.status === "COMPLETED" ? OUTCOME.passed_retest : null);
                          if (!oc) return <span className="text-slate-400">—</span>;
                          const { Icon } = oc;
                          return <span className={`inline-flex items-center gap-1 ${oc.tone}`}><Icon className="w-3.5 h-3.5" /> {oc.text}</span>;
                        })()}
                      </td>
                      <td className="py-2 px-4">
                        {o.status === "OPEN" && (o.current_rework_stage || 1) <= REWORK_STAGES && (
                          <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => openComplete(o)}
                            data-testid={`rwo-complete-${o.order_no}`}>
                            <Wrench className="w-3 h-3 mr-1" /> Complete Stage {o.current_rework_stage || 1}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={!!target} onOpenChange={(v) => { if (!v) setTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="rwo-complete-dialog">
          <DialogHeader>
            <DialogTitle>
              {target?.order_no} · Rework Stage {target?.current_rework_stage || 1} of {REWORK_STAGES}
            </DialogTitle>
          </DialogHeader>
          {target && (
            <div className="space-y-3 text-sm">
              <div className="text-slate-600">
                <span className="mono">{target.item_code}</span> · {target.item_name} — <strong>{inRework(target)}</strong> pcs in Rework
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>OK qty</Label>
                  <Input type="number" min="0" value={form.ok_qty} data-testid="rwo-ok-qty"
                    onChange={(e) => setForm({ ...form, ok_qty: e.target.value })} />
                  <p className="text-[11px] text-slate-500 mt-1">→ Final Inspection</p>
                </div>
                <div>
                  <Label>NOT OK qty</Label>
                  <Input type="number" min="0" value={form.not_ok_qty} data-testid="rwo-not-ok-qty"
                    onChange={(e) => setForm({ ...form, not_ok_qty: e.target.value })} />
                  <p className={`text-[11px] mt-1 ${(target.current_rework_stage || 1) >= REWORK_STAGES ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                    {(target.current_rework_stage || 1) >= REWORK_STAGES
                      ? "→ Rejected Warehouse"
                      : `→ Rework Stage ${(target.current_rework_stage || 1) + 1}`}
                  </p>
                </div>
              </div>
              <div>
                <Label>Final Inspection part number</Label>
                <select className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white" value={form.output_item_id}
                  onChange={(e) => setForm({ ...form, output_item_id: e.target.value })} data-testid="rwo-output-item">
                  <option value="">Auto (from Routing / BOM)</option>
                  {(fiItems || []).map((i) => <option key={i.id} value={i.id}>{i.code} — {i.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button onClick={submitStage} disabled={saving} data-testid="rwo-complete-submit">
              {saving ? "Saving…" : `Complete Stage ${target?.current_rework_stage || 1}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
